import {
  ChakraProvider,
  Box,
  Flex,
} from "@chakra-ui/react"
import SignInPage from "./view/SignInPage"
import React, { ReactNode, useEffect, useState } from "react"
import Header from "./base-components/Header"
import { BrowserRouter, Route, Routes } from "react-router-dom"
import { BASE_FE_PATH, RoutePath, SPACING } from "./generic/config"
import ProtectedRoute from "./generic/ProtectedRoute"
import DashboardPage from "./view/DashboardPage"
import { Language, TRANSLATIONS_EN, TRANSLATIONS_SE } from "./generic/Translations"
import Drawer from "./generic/Drawer"
import SystemSettingsPage from "./view/SystemSettingsPage"
import { UseQueryResult, useQuery } from "react-query"
import { QueryKeys } from "./api/base/react-query"
import { getProducts } from "./api/product-controller"
import Product from "./model/product"
import { getResources } from "./api/resource-controller"
import { getCompanySettings } from "./api/company-setting-controller"
import CommandPalette from "./base-components/CommandPalette"
import { useSelectedInstanceId } from "./generic/authStore"
import CompanySetting from "./model/company-setting"
import CustomerSearchPage from "./view/customer/CustomerSearchPage"
import Resource from "./model/resource"
import CustomerPage from "./view/customer/CustomerPage"
import SelectInstancePage from "./view/SelectInstancePage"
import { Toaster } from "./components/ui/toaster"
import DemoPage from "./view/DemoPage"
import WorksiteSearchPage from "./view/worksite/WorksiteSearchPage"
import WorksitePage from "./view/worksite/WorksitePage"
import OrderPage from "./view/order/OrderPage"
import OrderSearchPage from "./view/order/OrderSearchPage"
import { ColorModeProvider } from "./components/ui/color-mode"
import themeSystem from "./theme/chakra-theme"
import OverviewPage from "./view/overview/OverviewPage"
import { useAuth } from "react-oidc-context"
import TasksPage from "./view/overview/TasksPage"
import { isDesktop } from "react-device-detect"
import Divider from "./base-components/Divider"
import TemplateEditor from "./view/TemplateEditor"
import ProductSearchPage from "./view/product/ProductSearchPage"
import ProductPage from "./view/product/ProductPage"
import ResourceSearchPage from "./view/resource/ResourceSearchPage"
import ResourcePage from "./view/resource/ResourcePage"
import PropertySearchPage from "./view/property/PropertySearchPage"
import PropertyPage from "./view/property/PropertyPage"
import CustomerImportPage from "./view/import/CustomerImportPage"
import WorksiteImportPage from "./view/import/WorksiteImportPage"
import UserSearchPage from "./view/user/UserSearchPage"
import UserPage from "./view/user/UserPage"

// TODO: Fix or remove
// const system = createSystem(defaultConfig, {
//   theme: {
//     tokens: {
//       fonts: {
//         heading: { value: `'Figtree', sans-serif` },
//         body: { value: `'Figtree', sans-serif` },
//       },
//     },
//   },
// })

export const Context = React.createContext({
  language: Language.EN,
  setLanguage: (language: Language) => { },
  username: "",
  setUsername: (username: string) => { },
  translations: TRANSLATIONS_EN,

  products: null as UseQueryResult<Product[], unknown> | null,
  resources: null as UseQueryResult<Resource[], unknown> | null,
  companySettings: null as UseQueryResult<CompanySetting, unknown> | null,

});

export const DialogContext = React.createContext({
  dialogs: [] as JSX.Element[],
  setDialogs: (dialogs: JSX.Element[]) => { }
});

type RouteItem = {
  path: string,
  component: ReactNode
};

const protectedRoutes: RouteItem[] = [
  { path: RoutePath.Dashboard, component: <DashboardPage /> },
  { path: RoutePath.SystemSettings, component: <SystemSettingsPage /> },
  { path: RoutePath.Overview, component: <OverviewPage /> },
  { path: RoutePath.Tasks, component: <TasksPage /> },

  { path: RoutePath.Orders, component: <OrderSearchPage /> },
  { path: RoutePath.Orders + "/:id", component: <OrderPage /> },

  { path: RoutePath.Worksites, component: <WorksiteSearchPage /> },
  { path: RoutePath.Worksites + "/:id", component: <WorksitePage /> },

  { path: RoutePath.Customers, component: <CustomerSearchPage /> },
  { path: RoutePath.Customers + "/:id", component: <CustomerPage /> },

  { path: RoutePath.Products, component: <ProductSearchPage /> },
  { path: RoutePath.Products + "/:id", component: <ProductPage /> },

  { path: RoutePath.Resources, component: <ResourceSearchPage /> },
  { path: RoutePath.Resources + "/:id", component: <ResourcePage /> },

  { path: RoutePath.Properties, component: <PropertySearchPage /> },
  { path: RoutePath.Properties + "/:id", component: <PropertyPage /> },

  { path: RoutePath.CustomerImport, component: <CustomerImportPage /> },
  { path: RoutePath.WorksiteImport, component: <WorksiteImportPage /> },

  { path: RoutePath.Users, component: <UserSearchPage /> },
  { path: RoutePath.Users + "/:id", component: <UserPage /> },

  { path: RoutePath.SelectInstance, component: <SelectInstancePage /> },
  { path: RoutePath.Demo, component: <DemoPage /> },
  { path: RoutePath.TemplateEditor, component: <TemplateEditor /> },
  { path: "/", component: <DashboardPage /> },
];

export const App = () => {
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // Restored here (not in a header button) so the saved language applies on every page and layout.
  const [language, setLanguageInternal] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem("language");
      return saved === Language.SE || saved === Language.EN ? saved : Language.EN;
    } catch {
      return Language.EN;
    }
  });
  const setLanguage = (lang: Language) => {
    setLanguageInternal(lang);
    try { localStorage.setItem("language", lang); } catch { /* private mode: language just isn't remembered */ }
  }

  const auth = useAuth();
  const [username, setUsername] = useState("");
  useEffect(() => {
    const name = auth.user?.profile?.preferred_username;
    if (name) setUsername(name);
  }, [auth.user]);
  const [translations, setTranslations] = useState(TRANSLATIONS_EN);

  // Tenant data: only once signed in AND a company is chosen, keyed by the company. Fetching
  // earlier sent no X-Instance-Id (empty result), and that in-flight request could win the race
  // against the refetch after choosing — the company name then never appeared.
  // (react-query matches partial keys, so invalidateQueries(QueryKeys.products) still hits these.)
  const instanceId = useSelectedInstanceId();
  const enabled = auth.isAuthenticated && instanceId != null;
  const products = useQuery([QueryKeys.products, instanceId], () => getProducts(true), { enabled });
  const resources = useQuery([QueryKeys.resources, instanceId], () => getResources(true), { enabled });
  const companySettings = useQuery([QueryKeys.companySettings, instanceId], () => getCompanySettings(true), { enabled });

  const [dialogs, setDialogs] = useState([] as JSX.Element[]);

  // Set translations based on language
  useEffect(() => {
    if (language === Language.SE) {
      setTranslations(TRANSLATIONS_SE);
    } else {
      setTranslations(TRANSLATIONS_EN);
    }
  }, [language]);

  // Calculate the height of the header
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, [headerRef, headerHeight]);

  const contextValue = {
    language,
    setLanguage,
    username,
    setUsername,
    translations,
    products,
    resources,
    companySettings
  };

  return (
    <Provider>
      <Context.Provider value={contextValue}>
        <DialogContext.Provider value={{ dialogs, setDialogs }}>
          {dialogs.map((dialog, index) =>
          (
            <Box key={index} zIndex={100 + index}>
              {dialog}
            </Box>
          ))}
          <Box textAlign="center" fontSize="xl" height={"100vh"} display="flex" flexDir="column" >
            <BrowserRouter basename={BASE_FE_PATH}>
              <Header ref={headerRef} />
              {auth.isAuthenticated && <CommandPalette />}
              <Box minH={`${headerHeight}px`} mb={SPACING} />
              <Toaster />
              {/* Body */}
              <Routes>
                {protectedRoutes.map((route) =>
                (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <ProtectedRoute>
                        <Flex gap={SPACING} flex={1} mr={SPACING}>
                          {isDesktop && RoutePath.SelectInstance !== route.path && (
                            <Box as="nav" w="15rem" flexShrink={0} alignSelf="flex-start"
                                 position="sticky" top={0} maxH="100vh" overflowY="auto" overflowX="hidden">
                              {/* Drawer — fixed-width so labels never clip */}
                              <Drawer ratioInPercent={100} onSelect={null} />
                            </Box>
                          )}
                          <Box flex={1} minW={0} minH="100%" maxW={isDesktop ? undefined : "100vw"}>
                            {route.component}
                          </Box>
                        </Flex>
                      </ProtectedRoute>

                    } />
                ))}
                <Route path={RoutePath.Signin} Component={SignInPage} />
              </Routes>
            </BrowserRouter>
          </Box>
        </DialogContext.Provider>
      </Context.Provider>
    </Provider >
  )
}

export function Provider(props: any) {
  return (
    <ChakraProvider value={themeSystem}>
      <ColorModeProvider {...props} defaultTheme="light" />
    </ChakraProvider>
  )
}